import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import { VendorPaymentMethodDetailsResponse } from 'src/queries/vendors/vendors.types'
import { convertEnumToSentenceCase } from 'src/utils/string.utils'

import * as style from './PaymentInformationTab.style'
import { footerError, visuallyHidden } from './VendorFlyout.style'

const EM_DASH = '—'

/** Render a nullable/blank wire field as the prototype's em-dash, never
 * `undefined`. */
const orDash = (value: string | null | undefined): string =>
  value == null || value === '' ? EM_DASH : value

export interface VendorAchAccountCardProps {
  details:
    | Pick<
        VendorPaymentMethodDetailsResponse,
        'bankName' | 'accountType' | 'accountNumberLast4' | 'routingNumber'
      >
    | undefined
  isLoading: boolean
  isError: boolean
  /** Kebab / actions slot rendered in the header row; omit for no action. */
  headerAction?: React.ReactNode
  testId?: string
}

/**
 * The "Vendor ACH Account" details card — Bank Name / Account Type / Routing
 * Number / masked Account Number. Shared by the Payment Information tab's
 * confirmed-account state and both acknowledgement steps (EX-40844), so the
 * details a user confirms are rendered identically to the details they later
 * see on file.
 */
const VendorAchAccountCard: React.FC<VendorAchAccountCardProps> = ({
  details,
  isLoading,
  isError,
  headerAction,
  testId = 'vendor-ach-account-card',
}) => {
  const translate = useTranslate()

  const bankRows: { label: string; value: string }[] = [
    { label: translate('Bank Name'), value: orDash(details?.bankName) },
    {
      label: translate('Account Type'),
      // Modern Treasury returns this lower-cased (`checking` / `savings`) and
      // extend-api passes it through verbatim, so sentence-case it for display.
      // `&&` short-circuits null/undefined/'' straight into orDash's em-dash.
      value: orDash(
        details?.accountType && convertEnumToSentenceCase(details.accountType),
      ),
    },
    {
      label: translate('Routing Number'),
      value: orDash(details?.routingNumber),
    },
    {
      label: translate('Account Number'),
      // Non-breaking space (U+00A0) so the bullets and the last four never
      // wrap onto separate lines (EX-40842).
      value:
        details?.accountNumberLast4 == null
          ? EM_DASH
          : `••••\u00A0${details.accountNumberLast4}`,
    },
  ]

  return (
    <section css={style.card} data-testid={testId}>
      <div css={style.cardHeader}>
        <h3 css={style.cardTitle}>{translate('Vendor ACH Account')}</h3>
        {headerAction}
      </div>

      {/* ONE live region that stays mounted and swaps its content, rather than a
          live element that unmounts when the data lands. An unmounting region
          announces "Loading bank details…" and then nothing, so a screen-reader
          user is never told the values arrived — and on the acknowledgement step
          focus then moves to a checkbox asking them to attest to those very
          values (WCAG 2.1 AA 4.1.3). Visible while loading, visually hidden
          afterwards, so sighted users see the same single note as before. */}
      <p
        css={isLoading && !details ? style.sectionNote : visuallyHidden}
        role={'status'}
        aria-live={'polite'}
      >
        {isLoading && !details
          ? translate('Loading bank details…')
          : isError
            ? ''
            : translate('Bank details loaded.')}
      </p>

      {isLoading && !details ? null : (
        <>
          {isError && (
            // A provider (502) read failure is an error state, not a
            // blank card, and never the embed — the account still
            // exists. The rows stay, so the card still reads as an
            // account (em-dashed) with an explanation above it, rather
            // than collapsing to a lone error line.
            <p css={footerError} role={'alert'}>
              {translate(
                "We couldn't load this account's bank details right now. Please try again later.",
              )}
            </p>
          )}
          {/* A description list, not anonymous spans: these are label/value
              pairs and the pairing has to be programmatic, not just visual
              (WCAG 1.3.1). The grid styles are applied to dt/dd unchanged. */}
          <dl css={style.rows}>
            {bankRows.map(({ label, value }) => (
              <div css={style.row} key={label}>
                <dt css={style.rowLabel}>{label}</dt>
                <dd css={style.rowValue}>
                  {value === EM_DASH ? (
                    <>
                      <span aria-hidden={'true'}>{EM_DASH}</span>
                      {/* An em dash is silent under default screen-reader
                          punctuation settings, so the absence would otherwise
                          be announced as nothing at all. */}
                      <span css={visuallyHidden}>{translate('Not shown')}</span>
                    </>
                  ) : (
                    value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </section>
  )
}

export default VendorAchAccountCard
